import { useState } from "react";

/* ─── PIN dot row ─── */
function PinDots({ length, shaking }) {
  return (
    <div className={`flex justify-center gap-3 mb-5 ${shaking ? "animate-shake" : ""}`}>
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 flex items-center justify-center transition-all duration-150
            ${i < length
              ? "bg-amber-500 border-amber-500 scale-105"
              : "bg-stone-50 border-stone-300"
            }`}
        >
          {i < length && <span className="text-white text-lg leading-none">●</span>}
        </div>
      ))}
    </div>
  );
}

/* ─── Number pad ─── */
function NumPad({ onKey }) {
  const keys = ["1","2","3","4","5","6","7","8","9","","0","⌫"];
  return (
    <div className="grid grid-cols-3 gap-2">
      {keys.map((k, i) => {
        if (!k) return <div key={i} />;
        if (k === "⌫") {
          return (
            <button
              key={i}
              onClick={() => onKey("⌫")}
              className="h-12 sm:h-14 rounded-lg bg-red-50 border border-red-200 text-red-500 flex items-center justify-center transition-all duration-150 hover:bg-red-100 active:scale-95"
              aria-label="Delete"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"/>
              </svg>
            </button>
          );
        }
        return (
          <button
            key={i}
            onClick={() => onKey(k)}
            className="h-12 sm:h-14 rounded-lg bg-amber-50 border border-amber-200 font-handwritten font-bold text-xl text-amber-800 transition-all duration-150 hover:bg-amber-100 active:scale-95 active:bg-amber-200"
          >
            {k}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Force Reset PIN Screen ─── */
export function PlannerForceResetPin({ user, onComplete }) {
  const [step, setStep]     = useState(1);
  const [pin1, setPin1]     = useState("");
  const [pin2, setPin2]     = useState("");
  const [err, setErr]       = useState("");
  const [shaking, setShaking] = useState(false);

  function shake(msg) {
    setErr(msg);
    setShaking(true);
    setTimeout(() => { setShaking(false); setErr(""); }, 900);
  }

  function key1(k) {
    if (k === "⌫") { setPin1(p => p.slice(0, -1)); return; }
    const next = pin1 + k;
    if (next.length > 4) return;
    setPin1(next);
    if (next.length === 4) setStep(2);
  }

  function key2(k) {
    if (k === "⌫") { setPin2(p => p.slice(0, -1)); return; }
    const next = pin2 + k;
    if (next.length > 4) return;
    setPin2(next);
    if (next.length === 4) {
      if (next === pin1) {
        onComplete(pin1);
      } else {
        shake("PINs don't match — try again.");
        setTimeout(() => { setPin1(""); setPin2(""); setStep(1); }, 950);
      }
    }
  }

  const activePin = step === 1 ? pin1 : pin2;
  const activeKey = step === 1 ? key1 : key2;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden"
      style={{
        backgroundColor: "#c4956a",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23a67c52' fill-opacity='0.3'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3Cpath d='M6 5V0H5v5H0v1h5v94h1V6h94V5H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-8 left-8 text-3xl opacity-60 hidden sm:block" style={{ transform: "rotate(-25deg)" }}>✏️</div>
      <div className="absolute bottom-12 right-12 text-3xl opacity-60 hidden sm:block" style={{ transform: "rotate(15deg)" }}>📝</div>

      <div className="relative w-full max-w-xs">

        {/* ── Welcome header card ── */}
        <div className="paper-texture rounded-xl p-6 mb-6 relative index-card text-center">
          {/* Push pins */}
          <div className="absolute -top-2.5 left-8 w-5 h-5 rounded-full bg-green-500 shadow-md" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }} />
          <div className="absolute -top-2.5 right-8 w-5 h-5 rounded-full bg-purple-500 shadow-md" style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }} />
          {/* Washi tape */}
          <div
            className="absolute -top-3 left-1/2 w-20 h-6 rounded opacity-85"
            style={{
              background: "repeating-linear-gradient(-45deg, #f9a8d4, #f9a8d4 4px, #f472b6 4px, #f472b6 8px)",
              transform: "translateX(-50%) rotate(-1deg)",
            }}
          />
          <div className="pt-4">
            <div className="text-5xl mb-2">{user.avatar}</div>
            <h2 className="font-handwritten text-3xl text-amber-800">Welcome, {user.name}!</h2>
            <p className="text-stone-500 text-sm mt-2 leading-relaxed">
              {step === 1
                ? "Choose a new 4-digit PIN for your account. You'll use this every time you log in."
                : "Enter your new PIN again to confirm."}
            </p>
          </div>
        </div>

        {/* ── Step indicator ── */}
        <div className="flex justify-center gap-3 mb-4">
          <div className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full transition-all duration-200 ${step === 1 ? "bg-amber-200 text-amber-800" : "bg-stone-200 text-stone-500"}`}>
            <span>{step === 1 ? "🔐" : "✅"}</span>
            <span className="font-handwritten text-base">New PIN</span>
          </div>
          <div className="flex items-center text-stone-300 text-sm">→</div>
          <div className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full transition-all duration-200 ${step === 2 ? "bg-amber-200 text-amber-800" : "bg-stone-200 text-stone-400"}`}>
            <span>✅</span>
            <span className="font-handwritten text-base">Confirm</span>
          </div>
        </div>

        {/* ── PIN entry card ── */}
        <div className="paper-texture rounded-xl p-5 sm:p-6 relative index-card">
          {/* Push pin */}
          <div
            className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-yellow-400 shadow-md"
            style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)" }}
          />

          <div className="text-center mb-4 pt-1">
            <p className="font-handwritten text-xl text-stone-600">
              {step === 1 ? "Choose your PIN" : "Confirm your PIN"}
            </p>
          </div>

          <PinDots length={activePin.length} shaking={shaking} />
          <NumPad onKey={activeKey} />

          {err && (
            <p className="mt-3 text-center text-red-500 text-sm font-medium">{err}</p>
          )}

          {/* Start over button — only on step 2 */}
          {step === 2 && (
            <button
              onClick={() => { setStep(1); setPin1(""); setPin2(""); setErr(""); }}
              className="mt-4 w-full text-center text-stone-400 text-sm hover:text-stone-600 transition-colors duration-150"
            >
              ← Start over
            </button>
          )}
        </div>
      </div>

      {/* Shake animation */}
      <style>{`
        @keyframes planner-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: planner-shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
